import { Button, TableItem } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import SendButtonEmail from 'components/molecules/buttons/sendEmail/SendButtonEmail';
import LabeledEmailSend from 'components/molecules/labeledStatus/LabeledEmailSend';
import DownloadFilesInPeriodPopup from 'components/molecules/popup/download-files-in-period/DownloadFilesInPeriodPopup';
import { FooterTable, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useHasPermission } from 'hooks';
import { useStreamFileDownload } from 'hooks/useStreamFileDownload';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import { PiDownload } from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useSendEmail } from 'services/email/email';
import { DOCUMENT_TYPE } from 'services/email/types';
import {
  useDeleteReport,
  useGetLabelReportFile,
  useGetLabelReportFiles,
  useGetLabelReportsInfinite,
  useGetLabelReportsSearch,
} from 'services/label-report';
import { EMAIL_SEND } from 'services/types';
import { formatCurrency } from 'utils/global/currency.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

enum SORT_COLUMNS {
  startSettlementPeriod,
  endSettlementPeriod,
  label,
  number,
  net,
}

const Raport = () => {
  usePermissionGuard('LabelReportPermissionsLIST**', '/', true, 'statements');
  const hasPermission = useHasPermission();

  const { t } = useTranslation();

  const { setFile } = useStreamFileDownload(useGetLabelReportFile);

  const columnsVisibility = useTableColumnsVisibility(
    'GlobalRaports2',
    SORT_COLUMNS
  );

  const sendEmail = useSendEmail();

  const remove = useDeleteReport();

  const labelReportsQuery = useGetLabelReportsInfinite({});
  const labelReportsStatistics = useGetLabelReportsSearch();

  const netAmountLength = formatCurrency(
    labelReportsStatistics.data?.statistics.allAmountEarned
  ).length;

  const hasDetailPermission = hasPermission('LabelReportPermissionsLIST**', 16);

  const tableProps = useInfiniteTable(labelReportsQuery, { selectable: false });

  const onRemoveRaport = async (actions: PopupActions, id: string) =>
    await remove.mutateAsync(id, {
      onSuccess: async () => {
        await labelReportsQuery.refetch();
        await labelReportsStatistics.refetch();
        actions.close();
      },
      onError: error => onErrorShowAllErrorsToast(error),
    });

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`navbar.raport`)}
          hideBackButton
          topbarContent={
            <>
              <DownloadFilesInPeriodPopup
                service={useGetLabelReportFiles}
                filePrefix={t('navbar.raport')}
              />
              <Table.CustomColumnsButton
                columns={SORT_COLUMNS}
                isClear
                visibility={columnsVisibility}
                i18nextPrefix='input.label'
                customI18next=''
              />
            </>
          }
        />
      }>
      <FooterTable
        {...tableProps}
        id={columnsVisibility.tableId}
        columns={[
          {
            label: t('input.label.startSettlementPeriod'),
            renderCell: ({ startSettlementPeriodName }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(startSettlementPeriodName)}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.startSettlementPeriod),
            footer: t('table.total'),
          },
          {
            label: t('input.label.endSettlementPeriod'),
            renderCell: ({ endSettlementPeriodName }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(endSettlementPeriodName)}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.endSettlementPeriod),
          },
          {
            label: t('input.label.label'),
            renderCell: ({ labelName }) => (
              <TableItem>
                <TableItem.Text text={labelName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.label),
          },
          {
            label: t('input.label.number'),
            renderCell: ({ labelReportNumber }) => (
              <TableItem>
                <TableItem.Text text={labelReportNumber} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.number),
          },
          {
            label: t('input.label.net'),
            renderCell: item => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(item.amountEarned, {
                    length: netAmountLength,
                  })}
                />
              </TableItem>
            ),
            hide: columnsVisibility.hide(SORT_COLUMNS.net),
            footer: formatCurrency(
              labelReportsStatistics.data?.statistics.allAmountEarned
            ),
            resize: true,
          },
          {
            label: t('input.label.email-sender'),
            renderCell: ({ id, sendStatus }) => (
              <TableItem>
                <div className='flex items-center gap-2'>
                  {sendStatus !== EMAIL_SEND.notSend && (
                    <LabeledEmailSend status={sendStatus} />
                  )}
                  {sendStatus !== EMAIL_SEND.planned && (
                    <SendButtonEmail
                      documentId={id}
                      documentType={DOCUMENT_TYPE.labelReport}
                      refetch={labelReportsQuery.refetch}
                      status={sendStatus}
                    />
                  )}
                </div>
              </TableItem>
            ),
            resize: true,
          },
          {
            label: '',
            renderCell: ({ filePath, id, labelReportNumber, sendStatus }) => (
              <TableItem justifyPosition='end'>
                <div className='flex items-center'>
                  {hasDetailPermission && (
                    <Button
                      type='button'
                      onlyIcon
                      isClear
                      disabled={!filePath}
                      onClick={() =>
                        setFile({
                          id,
                          name: labelReportNumber ?? filePath ?? '',
                        })
                      }>
                      <Button.Icon icon={<PiDownload />} />
                      {/* <Button.Text text={t('button.download')} /> */}
                    </Button>
                  )}
                  <MagellanPopups.DeleteWarning
                    name={t('label-reports.what')}
                    onRemove={actions => onRemoveRaport(actions, id)}
                  />
                </div>
              </TableItem>
            ),
            resize: true,
          },
        ]}
      />
    </TopbarPage>
  );
};

export default Raport;
